/**
 * Repeating tables are saved as a JSON array in a single PDF text field, but the PDF
 * template lays them out as a grid of one widget per cell, named "<column> <rowLabel> <n>"
 * (e.g. "Rate of pay Row 1"). Those per-cell widgets are not part of the form's field
 * map, so nothing fills them on its own.
 *
 * Each entry maps the businessName of the field holding the array to the widget each row
 * property belongs in. Properties left out of `columns` stay out of the PDF — an
 * authorization id, for instance, is saved for the backend but printed as its description.
 */
export interface PdfTableWidgetMap {
  /** Word between the widget name and the row number. */
  rowLabel: string;
  /** How many rows the PDF lays out — rows past the saved ones are cleared, not left stale. */
  maxRows: number;
  /** Row property → the widget name it fills, without the row suffix. */
  columns: Record<string, string>;
}

export const PDF_TABLE_WIDGETS: Record<string, PdfTableWidgetMap> = {
  'Service Detail': {
    rowLabel: 'Row',
    maxRows: 7,
    columns: {
      serviceCodeDetail: 'Service Name and Service Code',
      serviceDescription: 'Description of each service',
      serviceDetail: 'Service Details',
      frequency: 'How often will the service be provided',
      units: 'Unit',
      price: 'Rate of pay',
    },
  },
};

/**
 * Every per-cell key a table has ever been addressed by, in both naming schemes that
 * exist: the form's control names ("<column id> Row 1") and the PDF's widget names
 * ("<widget label> Row 1"). Built once — the map is a module constant.
 */
const LEGACY_TABLE_CELL_KEYS: ReadonlySet<string> = new Set(
  Object.values(PDF_TABLE_WIDGETS).flatMap((map) =>
    Array.from({ length: map.maxRows }, (_, index) => index + 1).flatMap((row) =>
      Object.entries(map.columns).flatMap(([property, widget]) => [
        `${property} ${map.rowLabel} ${row}`,
        `${widget} ${map.rowLabel} ${row}`,
      ]),
    ),
  ),
);

/**
 * True for a per-cell table key such as "serviceDetail Row 1" or "Service Details Row 1".
 * The table's own carrier key ("Service Detail") is deliberately not matched.
 */
export function isLegacyTableCellKey(key: string): boolean {
  return LEGACY_TABLE_CELL_KEYS.has(key);
}

/**
 * Drop the per-cell table keys from an outbound form value.
 *
 * The PDF lays a table out as one widget per cell and the backend's field map still
 * declares those widgets, so they end up in the PDF form group and get echoed back in the
 * sign payload — alongside the carrier key that actually holds the rows. They are stale by
 * construction: the row data is read from the carrier key, so these only carry a duplicate
 * (or worse, a pre-edit) copy of it.
 *
 * Filtering here rather than leaving them out of the form group keeps the PDF rendering
 * path untouched — the widgets are still filled for display from PDF_TABLE_WIDGETS.
 */
export function stripLegacyTableCellKeys<T>(formValue: T): T {
  if (!formValue || typeof formValue !== 'object' || Array.isArray(formValue)) {
    return formValue;
  }

  const cleaned = Object.fromEntries(
    Object.entries(formValue as Record<string, unknown>).filter(
      ([key]) => !isLegacyTableCellKey(key),
    ),
  );

  return cleaned as T;
}
